import truncate from 'lodash/truncate';
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from 'semantic-ui-react';
import { Popup } from '../../lib/custom-ui';

import Paths from '../../constants/Paths';
import { ActivityTypes } from '../../constants/Enums';
import { useSteps } from '../../hooks';
import User from '../User';
import DeleteStep from '../DeleteStep';

import styles from './NotificationsStep.module.scss';

const StepTypes = {
  CLEAR_ALL: 'CLEAR_ALL',
};

const NotificationsStep = React.memo(({ items, onDelete, onDeleteAll, onClose }) => {
  const [t] = useTranslation();
  const [step, openStep, handleBack] = useSteps();

  const handleDelete = useCallback(
    (id) => {
      onDelete(id);
    },
    [onDelete],
  );

  const handleClearAllClick = useCallback(() => {
    openStep(StepTypes.CLEAR_ALL);
  }, [openStep]);

  const handleClearAllConfirm = useCallback(() => {
    if (items.length > 0) {
      onDeleteAll(items.map((item) => item.id));
    }

    onClose();
  }, [items, onDeleteAll, onClose]);

  const renderItemContent = useCallback(
    ({ activity, card }) => {
      switch (activity.type) {
        case ActivityTypes.MOVE_CARD:
          return (
            <Trans
              i18nKey="common.userMovedCardFromListToList"
              values={{
                user: activity.user.name,
                card: card.name,
                fromList: activity.data.fromList.name,
                toList: activity.data.toList.name,
              }}
            >
              {activity.user.name}
              {' moved '}
              <Link to={Paths.CARDS.replace(':id', card.id)} onClick={onClose}>
                {card.name}
              </Link>
              {' from '}
              {activity.data.fromList.name}
              {' to '}
              {activity.data.toList.name}
            </Trans>
          );
        case ActivityTypes.COMMENT_CARD: {
          const commentText = truncate(activity.data.text);

          return (
            <Trans
              i18nKey="common.userLeftNewCommentToCard"
              values={{
                user: activity.user.name,
                comment: commentText,
                card: card.name,
              }}
            >
              {activity.user.name}
              {` left a new comment «${commentText}» to `}
              <Link to={Paths.CARDS.replace(':id', card.id)} onClick={onClose}>
                {card.name}
              </Link>
            </Trans>
          );
        }
        default:
      }

      return null;
    },
    [onClose],
  );

  if (step) {
    switch (step.type) {
      case StepTypes.CLEAR_ALL:
        return (
          <DeleteStep
            title="common.clearNotifications"
            content="common.areYouSureYouWantToClearAllNotifications"
            buttonContent="action.clearAll"
            onConfirm={handleClearAllConfirm}
            onBack={handleBack}
          />
        );
      default:
    }
  }

  return (
    <>
      <Popup.Header>
        {t('common.notifications', {
          context: 'title',
        })}
      </Popup.Header>
      <Popup.Content>
        <div className={styles.actions}>
          <Button
            type="button"
            size="tiny"
            content={t('action.clearAll')}
            disabled={items.length === 0}
            onClick={handleClearAllClick}
          />
        </div>
        {items.length > 0 ? (
          <div className={styles.wrapper}>
            {items.map((item) => (
              <div key={item.id} className={styles.item}>
                {item.card && item.activity ? (
                  <>
                    <User
                      name={item.activity.user.name}
                      avatarUrl={item.activity.user.avatarUrl}
                      size="large"
                    />
                    <div className={styles.itemContent}>
                      <div className={styles.itemText}>{renderItemContent(item)}</div>
                      <div className={styles.itemDate}>
                        {t('format:longDateTime', {
                          postProcess: 'formatDate',
                          value: item.createdAt,
                        })}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className={styles.itemDeleted}>
                    <div className={styles.itemText}>{t('common.cardOrActionAreDeleted')}</div>
                    <div className={styles.itemDate}>
                      {t('format:longDateTime', {
                        postProcess: 'formatDate',
                        value: item.createdAt,
                      })}
                    </div>
                  </div>
                )}
                <Button
                  type="button"
                  icon="trash alternate outline"
                  className={styles.itemButton}
                  onClick={() => handleDelete(item.id)}
                />
              </div>
            ))}
          </div>
        ) : (
          t('common.noUnreadNotifications')
        )}
      </Popup.Content>
    </>
  );
});

NotificationsStep.propTypes = {
  items: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  onDelete: PropTypes.func.isRequired,
  onDeleteAll: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default NotificationsStep;
